import React, { useContext } from 'react';
import BaseModal from 'flow/components/ui-components/BaseModal';
import Icon from 'flow/components/ui-components/Icon';
import { twMerge } from 'tailwind-merge';
import AppContext from 'flow/contexts/app-context';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { hasMobileExtension } from 'flow/selectors/selectors';

import { selectOwnUserHasCapability } from 'flow/selectors/ExtensionSelectors';
import { CallMethodType, isRegisteredSIP } from 'flow/utils/call-utils';
import useExtension from 'flow/hooks/useExtension';
import Divider from './ui-components/Divider';
import RouteLink from './ui-components/RouteLink';
import { routes } from 'flow/types/routes';
import Checkbox from './ui-components/Checkbox';
import { setCallMethod } from 'flow/actions/app-actions';
import { Capabilities } from 'flow/utils/capability-utils';

interface Props {
    onCancel: () => void;
    onCall: (type: typeof CallMethodType) => void;
    forComonitor?: boolean;
}
type callMethodType = {
    type: typeof CallMethodType;
    disabled: boolean;
};

const CallMethodDialog: React.FC<Props> = ({
    onCancel,
    onCall,
    forComonitor,
}) => {
    const { i18next } = useContext(AppContext);

    const ownUserHasCapabilitySoftphone = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.SOFTPHONE),
    );

    const hasMex = useAppSelector(hasMobileExtension);
    const { ownExtension } = useExtension();
    const hasRegisteredTerminal = isRegisteredSIP(ownExtension);
    const [rememberMe, setRememberMe] = React.useState(false);
    const dispatch = useAppDispatch();

    const handleOnCall = (
        event: React.MouseEvent<HTMLButtonElement>,
        callMethodType: callMethodType,
    ) => {
        if (callMethodType) {
            if (rememberMe) {
                dispatch(setCallMethod(CallMethodType.get(callMethodType)));
            }
            event.preventDefault();
            onCall(CallMethodType.get(callMethodType));
        }
    };

    const callMethodTypes: {
        type: typeof CallMethodType;
        disabled: boolean;
    }[] = [];
    if (ownUserHasCapabilitySoftphone) {
        callMethodTypes.push({
            type: CallMethodType.LOCAL,
            disabled: false,
        });
    }
    callMethodTypes.push({
        type: CallMethodType.IP_PHONE,
        disabled: forComonitor || !hasRegisteredTerminal,
    });
    if (hasMex) {
        callMethodTypes.push({
            type: CallMethodType.MOBILE,
            disabled: forComonitor,
        });
    }

    return (
        <BaseModal
            open={true}
            header={i18next.t('General.CallMethodDialog.Title')}
            onClose={onCancel}
            disableActions={true}
            dataTestId={'call-method-dialog'}
        >
            <div className="py-4">
                {callMethodTypes.map((callMethod) => (
                    <button
                        data-test-id={`call-method-${callMethod.type.value}`}
                        onClick={
                            callMethod.disabled
                                ? undefined
                                : (e) => handleOnCall(e, callMethod.type.key)
                        }
                        key={callMethod.type.key}
                        className={twMerge(
                            'flex w-full gap-3 rounded p-2',
                            callMethod.disabled
                                ? 'opacity-50'
                                : 'cursor-pointer hover:bg-gray-100',
                        )}
                    >
                        <div className="inline-block rounded-lg bg-gray-200 p-1">
                            <Icon iconKey="call" size="1.75rem" />
                        </div>

                        <span className="self-center text-gray-800">
                            {i18next.t(
                                `settings.callmethod.${callMethod.type.value}`,
                            )}
                        </span>
                    </button>
                ))}
                <Divider className="my-3 dark:bg-gray-300" />
                <RouteLink
                    onClick={onCancel}
                    to={routes['settings.softphone.callmethod']}
                >
                    <p>{i18next.t('General.CallMethodDialog.ManageCalls')}</p>
                </RouteLink>
                <Divider className="my-3 dark:bg-gray-300" />
                <Checkbox
                    label={i18next.t('General.CallMethodDialog.Ask.Again')}
                    checked={rememberMe}
                    onChange={(event, value) => setRememberMe(value)}
                    className="flex h-13 w-full cursor-pointer items-center gap-3 rounded p-2 hover:bg-gray-100"
                />
            </div>
        </BaseModal>
    );
};

export default CallMethodDialog;
